import { useMemoizedFn } from 'ahooks';
import { useTranslateReport } from 'hooks/useTranslateReport';
import { LocalMessage } from '../conversation';
import { modeTranslate, overseaTranslate, TranslateMode } from 'services/api/overseaTranslate';
import sysLangToTranslationLang from 'utils/sysLangToTranslationLang';
import { useSystemLanguageValue } from '../system/useSystem';
import { useBatchUpdateMessages } from '../message';

export const useSingleOrBatchTrans = () => {
  const sysLang = useSystemLanguageValue();
  const batchUpdateMessages = useBatchUpdateMessages();
  const translateReport = useTranslateReport();

  const singleOrBatchTrans = useMemoizedFn(
    async (
      msg: LocalMessage | LocalMessage[],
      _lang?: General.Language,
      mode?: TranslateMode,
      translated?: boolean,
    ) => {
      const lang = _lang || sysLang;
      const languageKey = sysLangToTranslationLang(lang);

      let source: Conversation.Translation['source'] = 'chatpp';

      const isList = Array.isArray(msg);
      if (!isList) {
        const { messageType } = msg;
        if (messageType === 'ATTACHMENT') {
          source = msg.attachmentContent?.autoTranslation?.source ? 'sender' : 'chatpp';
        } else {
          source = msg.textContent?.autoTranslation?.source ? 'sender' : 'chatpp';
        }
      }

      const msgs = (isList ? msg : [msg]).filter((msg) => {
        return msg.messageType === 'ATTACHMENT'
          ? !['pending'].includes(msg.attachmentContent?.translationContent?.[languageKey]?.status)
          : !['pending'].includes(msg.textContent?.translationContent?.[languageKey]?.status);
      });

      if (msgs.length === 0) {
        return;
      }

      batchUpdateMessages(
        msgs.map((_msg) => {
          return [
            _msg.msgId,
            (msg) => {
              if (!msg) {
                return;
              }
              const { textContent, attachmentContent, messageType } = msg;
              const translationContent = {
                ...(messageType === 'ATTACHMENT'
                  ? attachmentContent?.translationContent
                  : textContent?.translationContent),
                [languageKey]: {
                  language: languageKey,
                  status: 'pending',
                  text: '',
                  translated,
                },
              };

              if (messageType === 'ATTACHMENT') {
                return {
                  ...msg,
                  hideTranslate: false,
                  attachmentContent: {
                    ...(attachmentContent as any),
                    translationContent,
                  },
                };
              } else {
                return {
                  ...msg,
                  hideTranslate: false,
                  textContent: {
                    ...(textContent as any),
                    translationContent,
                  },
                };
              }
            },
          ];
        }),
      );

      const texts = msgs.map((msg) => {
        if (msg.messageType === 'ATTACHMENT') {
          return msg.attachmentContent?.text || '';
        }
        return msg.textContent?.text || msg.imageWithTextContent?.text || '';
      });

      try {
        const transed = await (mode
          ? modeTranslate(texts, lang, mode, { isUseRedisCache: !translated })
          : overseaTranslate(texts, lang, 'chat', msgs[0].region));

        batchUpdateMessages(
          msgs.map((_msg, index) => {
            return [
              _msg.msgId,
              (msg) => {
                if (!msg) {
                  return;
                }
                const { msgId, textContent, messageType, attachmentContent } = msg;
                const text = transed[index];

                if (!msgId.startsWith('nim-')) {
                  translateReport(
                    {
                      msgId,
                      translation: {
                        text,
                        source,
                        targetLanguage: languageKey,
                      },
                    },
                    'Message',
                  );
                }

                if (messageType === 'ATTACHMENT') {
                  return {
                    ...msg,
                    attachmentContent: {
                      ...(attachmentContent as any),
                      translationContent: {
                        ...attachmentContent?.translationContent,
                        [languageKey]: {
                          text,
                          source,
                          targetLanguage: languageKey,
                          status: 'success',
                          translated,
                          mode,
                        },
                      },
                    },
                  };
                } else {
                  return {
                    ...msg,
                    textContent: {
                      ...(textContent as any),
                      translationContent: {
                        ...textContent?.translationContent,
                        [languageKey]: {
                          text,
                          source,
                          targetLanguage: languageKey,
                          status: 'success',
                          translated,
                          mode,
                        },
                      },
                    },
                  };
                }
              },
            ];
          }),
        );

        return transed;
      } catch (_e) {
        batchUpdateMessages(
          msgs.map((_msg) => {
            return [
              _msg.msgId,
              (msg) => {
                if (!msg) {
                  return;
                }
                const { textContent, messageType, attachmentContent } = msg;
                if (messageType === 'ATTACHMENT') {
                  return {
                    ...msg,
                    textContent: {
                      ...(attachmentContent as any),
                      translationContent: {
                        ...attachmentContent?.translationContent,
                        [languageKey]: {
                          language: languageKey,
                          status: 'error',
                          text: '',
                        },
                      },
                    },
                  };
                } else {
                  return {
                    ...msg,
                    textContent: {
                      ...(textContent as any),
                      translationContent: {
                        ...textContent?.translationContent,
                        [languageKey]: {
                          language: languageKey,
                          status: 'error',
                          text: '',
                        },
                      },
                    },
                  };
                }
              },
            ];
          }),
        );

        throw _e;
      }
    },
  );

  return singleOrBatchTrans;
};
